import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { masterproSearchPath } from '../../../utils/proSearch';

export interface PageHeaderProps {
  /** Numeric order pronumber, or a "T"-prefixed template pronumber ("T110"). */
  pronumber: number | string;
  /** When true, renders a "TEMPLATE" badge next to the pronumber. */
  isTemplate?: boolean;
  /** When true, the record is soft-deleted — shows a red DELETED banner + badge. */
  deleted?: boolean;
  /** Move type label, e.g. "Import" — rendered as a pill. */
  movetype?: string;
  /** Classification label, e.g. "Key Accounts 1" — rendered as a pill. */
  classification?: string;
  masterPro?: number | null;
  equipmentNumber?: string | null;
  billTo?: { name: string; code?: string | number } | null;
  customer?: { name: string; code?: string | number } | null;
  /** Position within a result set; when present, prev/next nav is shown. */
  position?: { current: number; total: number };
  onPrev?: () => void;
  onNext?: () => void;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  pronumber,
  isTemplate,
  deleted,
  movetype,
  classification,
  masterPro,
  equipmentNumber,
  billTo,
  customer,
  position,
  onPrev,
  onNext,
}) => {
  return (
    <div className="bg-cmp-page-header border-b border-cmp-page-header-edge">
      {/* Deleted banner — bright red so a deleted record is obvious at a glance. */}
      {deleted && (
        <div className="bg-red-600 text-white px-6 py-1 text-[0.75rem] font-bold uppercase tracking-widest">
          Deleted
        </div>
      )}
      {/* Title row */}
      <div className="flex items-center justify-between gap-4 px-6 py-3">
        <h1 className="m-0 flex items-center gap-2.5 text-[1.0625rem] font-semibold tracking-tight text-cmp-page-header-fg min-w-0">
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className="font-semibold text-blue-600 whitespace-nowrap hover:underline"
          >
            Pro# {pronumber}
          </a>
          {deleted && (
            <span className="px-2.5 py-0.5 text-[0.6875rem] font-bold uppercase tracking-wider rounded bg-red-600 text-white border border-red-700">
              Deleted
            </span>
          )}
          {isTemplate && (
            <span className="px-2.5 py-0.5 text-[0.6875rem] font-bold uppercase tracking-wider rounded bg-amber-100 text-amber-900 border border-amber-300">
              Template
            </span>
          )}
          {movetype && (
            <span className="px-2.5 py-0.5 text-[0.6875rem] font-bold uppercase tracking-wider rounded bg-orange-50 text-orange-800 border border-orange-300">
              {movetype}
            </span>
          )}
          {classification && (
            <span className="px-2.5 py-0.5 text-[0.6875rem] font-bold uppercase tracking-wider rounded bg-indigo-50 text-indigo-800 border border-indigo-200">
              {classification}
            </span>
          )}
        </h1>

        {position && (
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="inline-flex items-stretch border border-slate-200 rounded-md overflow-hidden bg-white">
              <button
                type="button"
                aria-label="Previous"
                onClick={onPrev}
                disabled={!onPrev}
                className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1 border-r border-slate-200"
              >
                <ChevronLeftIcon className="w-3 h-3" strokeWidth={2.5} />
                Prev
              </button>
              <span className="px-2.5 py-1 text-xs font-semibold text-slate-900 border-r border-slate-200 whitespace-nowrap inline-flex items-center">
                {position.current} of {position.total}
              </span>
              <button
                type="button"
                aria-label="Next"
                onClick={onNext}
                disabled={!onNext}
                className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1"
              >
                Next
                <ChevronRightIcon className="w-3 h-3" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Meta row */}
      <div className="px-6 py-2 flex flex-wrap gap-x-7 gap-y-1 text-xs text-muted">
        {masterPro != null && (
          <div className="flex items-center gap-1.5">
            <span className="text-[0.6875rem] uppercase tracking-wider text-slate-500">Master Pro</span>
            <a
              href={masterproSearchPath(masterPro)}
              target="_blank"
              rel="noopener noreferrer"
              title="All PROs on this Master PRO (new tab)"
              className="font-semibold text-blue-600 hover:underline"
            >
              {masterPro}
            </a>
          </div>
        )}
        {equipmentNumber && (
          <div className="flex items-center gap-1.5">
            <span className="text-[0.6875rem] uppercase tracking-wider text-slate-500">Equipment #</span>
            <span className="font-semibold text-heading">{equipmentNumber}</span>
          </div>
        )}
        {billTo && (
          <div className="flex items-center gap-1.5">
            <span className="text-[0.6875rem] uppercase tracking-wider text-slate-500">Bill To</span>
            <span className="font-semibold text-slate-900">{billTo.name}</span>
            {billTo.code != null && billTo.code !== '' && (
              <span className="text-slate-400">{billTo.code}</span>
            )}
          </div>
        )}
        {customer && (
          <div className="flex items-center gap-1.5">
            <span className="text-[0.6875rem] uppercase tracking-wider text-slate-500">Customer</span>
            <span className="font-semibold text-slate-900">{customer.name}</span>
            {customer.code != null && customer.code !== '' && (
              <span className="text-slate-400">{customer.code}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
